import React, { useState } from "react";
import { toast } from "sonner";
import { Swords, Loader2, Target, ListChecks } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { showXPToast } from "@/lib/xpToast";

/** Student-facing capstone project + AI-graded submission for a course. */
export function BossBattlePanel({ courseId, battles, onSubmitted }) {
  const [drafts, setDrafts] = useState({});
  const [submittingId, setSubmittingId] = useState(null);

  const submit = async (battleId) => {
    const submission_text = (drafts[battleId] || "").trim();
    if (!submission_text) { toast.error("Describe or link your submission"); return; }
    setSubmittingId(battleId);
    try {
      await api.post(`/courses/${courseId}/boss-battles/${battleId}/submit`, { submission_text });
      toast.success("Boss Battle submitted — AI graded it instantly!");
      showXPToast(0, 0, "Boss Battle Defeated");
      onSubmitted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmittingId(null);
    }
  };

  if (!battles || battles.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8" data-testid="boss-battle-panel-empty">No Boss Battle configured for this course yet.</p>;
  }

  return (
    <div className="space-y-4" data-testid="boss-battle-panel">
      {battles.map((b) => (
        <div key={b.id} className="bg-white/[0.03] border border-amber-400/20 rounded-xl p-4" data-testid={`boss-battle-card-${b.id}`}>
          <p className="text-sm font-bold text-white flex items-center gap-2"><Swords className="w-4 h-4 text-amber-400" /> {b.title}</p>
          <p className="text-xs text-slate-400 mt-2 flex items-start gap-1.5"><Target className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-300" /> {b.goal_description}</p>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mt-3 flex items-center gap-1.5"><ListChecks className="w-3 h-3" /> Grading Rubric</p>
          <p className="text-xs text-slate-400 mt-1 whitespace-pre-wrap">{b.grading_rubric}</p>

          {b.my_submission ? (
            <div className="mt-3 bg-amber-400/5 border border-amber-400/20 rounded-lg p-3">
              <p className="text-sm font-bold text-amber-300" data-testid={`boss-battle-score-${b.id}`}>AI Score: {b.my_submission.ai_score}/100</p>
              <p className="text-xs text-slate-300 mt-1.5">{b.my_submission.ai_feedback}</p>
            </div>
          ) : (
            <div className="mt-3">
              <textarea
                value={drafts[b.id] || ""} onChange={(e) => setDrafts((d) => ({ ...d, [b.id]: e.target.value }))}
                placeholder="Paste your project link or describe what you built..." rows={3} data-testid={`boss-battle-submit-input-${b.id}`}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none"
              />
              <button onClick={() => submit(b.id)} disabled={submittingId === b.id} data-testid={`boss-battle-submit-btn-${b.id}`}
                className="mt-2 flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black text-xs font-bold px-4 py-2 rounded-lg">
                {submittingId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Submit for AI Grading"}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
